import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";
import { Button } from "clutch/src/Button/Button.jsx";

import { readState } from "@/__main__/app-state.mjs";
import { IS_APP } from "@/__main__/constants.mjs";
import {
  GAME_PREFERRED_NAME_MAP,
  GAME_SHORT_NAMES,
  I18N_PLACEHOLDER,
} from "@/app/constants.mjs";
import appRefs from "@/app/refs.mjs";
import { LINKED_ACCOUNT_EMPTY_STATE_ROBOT } from "@/hub-settings/constants.mjs";
import { launchGame } from "@/library/actions.mjs";
import { classNames } from "@/util/class-names.mjs";
import { devError } from "@/util/dev.mjs";
import { useGameSymbol } from "@/util/game-route.mjs";
import { useInstalledGames } from "@/util/game-system-hooks.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const Onboarding = styled("div")`
  width: 100%;
  background: var(--shade10);
  border-bottom: 1px var(--shade8) solid;
  display: flex;
  justify-content: center;
  --header-container: var(--sp-container);

  & > div {
    padding: var(--sp-1_5) 0;
    max-width: var(--header-container);
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--sp-3);
  }

  video {
    height: var(--sp-14);
    margin: calc(-1 * var(--sp-3));
  }

  .description {
    /*flex: 1;*/
  }
`;

export default function OnboardWidget() {
  const {
    appShell: { titleBarClasses },
  } = useSnapshot(appRefs);
  const { settings } = useSnapshot(readState);
  const gameSymbol = useGameSymbol();
  const installedGames = useInstalledGames();
  const [launchingState, setLaunchingState] = useState(false);
  const { t } = useTranslation();
  const doLaunch = useCallback(
    (gameSymbol) => {
      if (launchingState) return;
      setLaunchingState(true);
      setTimeout(() => {
        setLaunchingState(false);
      }, 5 * 1000);
      launchGame(gameSymbol).catch(devError);
    },
    [launchingState],
  );

  const shortName = GAME_SHORT_NAMES[gameSymbol];

  // Really this sanity check for settings shouldn't be needed,
  // but sometimes this component renders before the app initializes,
  // OR settings is written to null for insane reasons.
  const hasPlayed =
    settings &&
    (settings.lastLoggedInIdByGame[shortName] ||
      settings.lastPlayedGames[shortName]);

  if (
    !IS_APP ||
    !gameSymbol ||
    !installedGames.includes(gameSymbol) ||
    hasPlayed
  )
    return null;

  const gameName = GAME_PREFERRED_NAME_MAP[gameSymbol]
    ? t(...GAME_PREFERRED_NAME_MAP[gameSymbol])
    : I18N_PLACEHOLDER;
  let launchLabel = t("common:launchGame", "Launch Game");
  if (launchingState) launchLabel = t("common:loading", "Loading...");
  return (
    <Onboarding {...classNames(...titleBarClasses)}>
      <div>
        <video loop autoPlay preload="metadata" controlsList="nodownload" muted>
          <source
            src={`${LINKED_ACCOUNT_EMPTY_STATE_ROBOT}`}
            type="video/mp4"
          />
        </video>
        <div className="description type-body2">
          {t(
            "common:launchGameLong",
            "Launch {{game}} and log in to the account that you want to connect",
            { game: gameName },
          )}
        </div>
        <Button
          onClick={() => doLaunch(gameSymbol)}
          textClass="type-body2"
          textColor="var(--yellow)"
          size="small"
          disabled={launchingState ? true : null}
        >
          {launchLabel}
        </Button>
      </div>
    </Onboarding>
  );
}
